import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Tabs, // <-- Ensure this is imported
  Tab, // <-- Ensure this is imported
  Chip,
  LinearProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Key as KeyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  VpnKey as TokenIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as InviteIcon,
  Block as BlockIcon,
  Group as GroupIcon,
  AssignmentInd as AssignmentIndIcon,
  ListAlt as ListAltIcon,
  Edit as EditIcon, // Move EditIcon import here
} from '@mui/icons-material';
import FullscreenButton from './FullscreenButton';
import UserManagement from './UserManagement';
import PermissionMatrixTab from './PermissionMatrixTab';

function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', permissions: [] });
  const [permissions, setPermissions] = useState([]);
  const [error, setError] = useState('');

  const loadRoles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('tkn');
      const response = await fetch('/api/v1/roles', { // Fixed: Added v1
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles || []);
        console.log('✅ Successfully loaded roles');
      } else {
        setError('Failed to load roles');
        console.error('❌ Load roles failed:', response.status);
      }
    } catch (err) {
      setError('Error loading roles');
      console.error('❌ Load roles error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('tkn');
      const response = await fetch('/api/v1/permissions', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || []);
        console.log('✅ Successfully loaded permissions');
      } else {
        setError('Failed to load permissions');
        console.error('❌ Load permissions failed:', response.status);
      }
    } catch (err) {
      setError('Error loading permissions');
      console.error('❌ Load permissions error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  const handleSave = async () => {
    try {
      const method = selectedRole ? 'PUT' : 'POST';
      const url = selectedRole ? `/api/v1/roles/${selectedRole._id}` : '/api/v1/roles';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to save role');
      setEditDialog(false);
      setForm({ name: '', description: '', permissions: [] });
      setSelectedRole(null);
      loadRoles();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm('Delete this role?')) return;
    await fetch(`/api/v1/roles/${role._id}`, { method: 'DELETE' });
    loadRoles();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Roles Management</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditDialog(true); setSelectedRole(null); setForm({ name: '', description: '', permissions: [] }); }}>
          Add Role
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role._id}>
                <TableCell>{role.name}</TableCell>
                <TableCell>{role.description}</TableCell>
                <TableCell>
                  {(role.permissions || []).join(', ')}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => { setSelectedRole(role); setForm(role); setEditDialog(true); }}><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDelete(role)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedRole ? 'Edit Role' : 'Add Role'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Permissions"
            select
            SelectProps={{ multiple: true }}
            value={form.permissions}
            onChange={(e) => setForm((f) => ({ ...f, permissions: e.target.value }))}
            fullWidth
          >
            {permissions.map((perm) => (
              <MenuItem key={perm._id} value={perm.name}>{perm.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">{selectedRole ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function PermissionsTab() {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: '' });
  const [error, setError] = useState('');

  const loadPermissions = async () => {
    setLoading(true);
    const res = await fetch('/api/v1/permissions');
    const data = await res.json();
    setPermissions(data.permissions || []);
    setLoading(false);
  };

  useEffect(() => {
    loadPermissions();
  }, []);

  const handleSave = async () => {
    try {
      const method = selectedPermission ? 'PUT' : 'POST';
      const url = selectedPermission ? `/api/v1/permissions/${selectedPermission._id}` : '/api/v1/permissions';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to save permission');
      setEditDialog(false);
      setForm({ name: '', description: '', category: '' });
      setSelectedPermission(null);
      loadPermissions();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (perm) => {
    if (!window.confirm('Delete this permission?')) return;
    await fetch(`/api/v1/permissions/${perm._id}`, { method: 'DELETE' });
    loadPermissions();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Permissions Management</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditDialog(true); setSelectedPermission(null); setForm({ name: '', description: '', category: '' }); }}>
          Add Permission
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map((perm) => (
              <TableRow key={perm._id}>
                <TableCell>{perm.name}</TableCell>
                <TableCell>{perm.description}</TableCell>
                <TableCell>{perm.category}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => { setSelectedPermission(perm); setForm(perm); setEditDialog(true); }}><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDelete(perm)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedPermission ? 'Edit Permission' : 'Add Permission'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Category"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">{selectedPermission ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Security() {
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: 30,
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
    },
    loginAttempts: {
      maxAttempts: 5,
      lockoutDuration: 15,
    },
    ipWhitelist: [],
    apiRateLimit: {
      enabled: true,
      requestsPerMinute: 100,
    },
  });

  const [securityLogs, setSecurityLogs] = useState([]);
  const [activeTokens, setActiveTokens] = useState([]);
  const [suspiciousActivity, setSuspiciousActivity] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newIpAddress, setNewIpAddress] = useState('');
  const [securityScore, setSecurityScore] = useState(85);
  const [tab, setTab] = useState(0);

  // Mock data loading
  useEffect(() => {
    const mockLogs = [
      {
        id: '1',
        timestamp: new Date(Date.now() - 3600000),
        event: 'Login Success',
        user: 'john.doe@example.com',
        ip: '192.168.1.100',
        severity: 'info',
      },
      {
        id: '2',
        timestamp: new Date(Date.now() - 7200000),
        event: 'Failed Login Attempt',
        user: 'unknown@hacker.com',
        ip: '185.220.101.5',
        severity: 'warning',
      },
      {
        id: '3',
        timestamp: new Date(Date.now() - 10800000),
        event: 'API Key Generated',
        user: 'admin@example.com',
        ip: '192.168.1.10',
        severity: 'info',
      },
      {
        id: '4',
        timestamp: new Date(Date.now() - 14400000),
        event: 'Multiple Failed Logins',
        user: 'test@test.com',
        ip: '10.0.0.1',
        severity: 'error',
      },
    ];

    const mockTokens = [
      {
        id: '1',
        name: 'Mobile App Token',
        created: new Date(Date.now() - 86400000 * 7),
        lastUsed: new Date(Date.now() - 3600000),
        permissions: ['read', 'write'],
        status: 'active',
      },
      {
        id: '2',
        name: 'API Integration',
        created: new Date(Date.now() - 86400000 * 30),
        lastUsed: new Date(Date.now() - 86400000 * 2),
        permissions: ['read'],
        status: 'active',
      },
      {
        id: '3',
        name: 'Development Token',
        created: new Date(Date.now() - 86400000 * 3),
        lastUsed: new Date(Date.now() - 86400000),
        permissions: ['read', 'write', 'admin'],
        status: 'expired',
      },
    ];

    const mockSuspicious = [
      {
        id: '1',
        description: 'Multiple failed login attempts from IP: 185.220.101.5',
        severity: 'high',
        timestamp: new Date(Date.now() - 7200000),
        status: 'investigating',
      },
      {
        id: '2',
        description: 'Unusual API usage pattern detected',
        severity: 'medium',
        timestamp: new Date(Date.now() - 14400000),
        status: 'resolved',
      },
    ];

    setSecurityLogs(mockLogs);
    setActiveTokens(mockTokens);
    setSuspiciousActivity(mockSuspicious);
  }, []);

  const handleSettingChange = (setting, value) => {
    setSecuritySettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  const handlePasswordPolicyChange = (field, value) => {
    setSecuritySettings((prev) => ({
      ...prev,
      passwordPolicy: {
        ...prev.passwordPolicy,
        [field]: value,
      },
    }));
  };

  const addIpToWhitelist = () => {
    if (newIpAddress.trim()) {
      setSecuritySettings((prev) => ({
        ...prev,
        ipWhitelist: [...prev.ipWhitelist, newIpAddress.trim()],
      }));
      setNewIpAddress('');
      setDialogOpen(false);
    }
  };

  const removeIpFromWhitelist = (ip) => {
    setSecuritySettings((prev) => ({
      ...prev,
      ipWhitelist: prev.ipWhitelist.filter((item) => item !== ip),
    }));
  };

  const revokeToken = (tokenId) => {
    setActiveTokens((prev) => prev.filter((token) => token.id !== tokenId));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getSecurityScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h4" component="h1">
          Security Center
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FullscreenButton />
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab icon={<SecurityIcon />} label="Security" />
        <Tab icon={<AssignmentIndIcon />} label="User Management" />
        <Tab icon={<GroupIcon />} label="Roles" />
        <Tab icon={<ListAltIcon />} label="Permissions" />
        <Tab icon={<SecurityIcon />} label="Permission Matrix" />
      </Tabs>

      {tab === 0 && (
        <Box>
          {/* Security Score */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, mb: 2,
              }}
              >
                <SecurityIcon color="primary" />
                <Typography variant="h6">Security Score</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={securityScore}
                    color={getSecurityScoreColor(securityScore)}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Typography variant="h5" color={`${getSecurityScoreColor(securityScore)}.main`}>
                  {securityScore}
                  %
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Your security configuration is strong. Consider enabling 2FA for better protection.
              </Typography>
            </CardContent>
          </Card>

          {/* Security Settings */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <ShieldIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Authentication Settings
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Two-Factor Authentication"
                        secondary="Add an extra layer of security to your account"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={securitySettings.twoFactorAuth}
                          onChange={(e) => handleSettingChange('twoFactorAuth', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Session Timeout"
                        secondary={`Auto-logout after ${securitySettings.sessionTimeout} minutes of inactivity`}
                      />
                      <ListItemSecondaryAction>
                        <TextField
                          size="small"
                          type="number"
                          value={securitySettings.sessionTimeout}
                          onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                          inputProps={{ min: 5, max: 120, style: { width: '60px' } }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <LockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Password Policy
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Minimum Length"
                        secondary="Require passwords to be at least this many characters"
                      />
                      <ListItemSecondaryAction>
                        <TextField
                          size="small"
                          type="number"
                          value={securitySettings.passwordPolicy.minLength}
                          onChange={(e) => handlePasswordPolicyChange('minLength', parseInt(e.target.value))}
                          inputProps={{ min: 6, max: 32, style: { width: '60px' } }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Require Uppercase Letters" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={securitySettings.passwordPolicy.requireUppercase}
                          onChange={(e) => handlePasswordPolicyChange('requireUppercase', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Require Numbers" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={securitySettings.passwordPolicy.requireNumbers}
                          onChange={(e) => handlePasswordPolicyChange('requireNumbers', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Require Special Characters" />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={securitySettings.passwordPolicy.requireSpecialChars}
                          onChange={(e) => handlePasswordPolicyChange('requireSpecialChars', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* IP Whitelist */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
              }}
              >
                <Typography variant="h6">
                  <AdminIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  IP Whitelist
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setDialogOpen(true)}
                >
                  Add IP
                </Button>
              </Box>
              {securitySettings.ipWhitelist.length === 0 ? (
                <Typography color="text.secondary">
                  No IP addresses in whitelist. All IPs are allowed.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {securitySettings.ipWhitelist.map((ip, index) => (
                    <Chip
                      key={index}
                      label={ip}
                      onDelete={() => removeIpFromWhitelist(ip)}
                      deleteIcon={<DeleteIcon />}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Active Tokens */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TokenIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Active API Tokens
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Last Used</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeTokens.map((token) => (
                      <TableRow key={token.id}>
                        <TableCell>{token.name}</TableCell>
                        <TableCell>{token.created.toLocaleDateString()}</TableCell>
                        <TableCell>{token.lastUsed.toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {token.permissions.map((perm) => (
                              <Chip key={perm} label={perm} size="small" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={token.status}
                            color={token.status === 'active' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => revokeToken(token.id)}
                            disabled={token.status !== 'active'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Security Logs */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Security Logs
                  </Typography>
                  <List>
                    {securityLogs.map((log) => (
                      <ListItem key={log.id} divider>
                        <ListItemIcon>
                          {log.severity === 'error' && <ErrorIcon color="error" />}
                          {log.severity === 'warning' && <WarningIcon color="warning" />}
                          {log.severity === 'info' && <CheckCircleIcon color="success" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={log.event}
                          // Fix: avoid <div> inside <p> by using only inline elements for secondary
                          secondary={(
                            <>
                              <span>
                                <Typography component="span" variant="body2">
                                  User:
                                  {' '}
                                  {log.user}
                                  {' '}
                                  • IP:
                                  {' '}
                                  {log.ip}
                                </Typography>
                              </span>
                              <br />
                              <span>
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {log.timestamp.toLocaleString()}
                                </Typography>
                              </span>
                            </>
                          )}
                        />
                        <ListItemSecondaryAction>
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Suspicious Activity
                  </Typography>
                  {suspiciousActivity.map((activity) => (
                    <Alert
                      key={activity.id}
                      severity={activity.severity}
                      sx={{ mb: 2 }}
                      action={(
                        <Button size="small">
                          Investigate
                        </Button>
                      )}
                    >
                      <Typography variant="body2">
                        {activity.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activity.timestamp.toLocaleString()}
                      </Typography>
                    </Alert>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Add IP Dialog */}
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
            <DialogTitle>Add IP Address to Whitelist</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="IP Address"
                fullWidth
                variant="outlined"
                value={newIpAddress}
                onChange={(e) => setNewIpAddress(e.target.value)}
                placeholder="192.168.1.1"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={addIpToWhitelist} variant="contained">Add</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      {tab === 1 && (
        <UserManagement />
      )}
      {tab === 2 && (
        <RolesTab />
      )}
      {tab === 3 && (
        <PermissionsTab />
      )}
      {tab === 4 && (
        <PermissionMatrixTab />
      )}
    </Box>
  );
}

export default Security;
